<template>
  <v-app>
    <v-container fluid>
      <!-- Search Form -->
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Search Employee Loans
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submitSearch">
                  <v-row class="pa-2">
                    <v-col lg="3" md="4" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="from_date.value.value"
                        label="From Date"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        clearable
                        :error-messages="from_date.errorMessage.value"
                      />
                    </v-col>

                    <v-col lg="3" md="4" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="to_date.value.value"
                        label="To Date"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        clearable
                        :error-messages="to_date.errorMessage.value"
                      />
                    </v-col>

                    <v-col lg="4" md="4" cols="12">
                      <v-autocomplete
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="employee_id.value.value"
                        :items="getHrmEmployeeList"
                        :item-title="(item) => `${item.name} - ${item.code || ''}`"
                        item-value="id"
                        label="Employee"
                        clearable
                        placeholder="All Employees"
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
/>
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn class="bg-brand" type="submit" ref="searchButton">
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Loan Records Table -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title class="d-flex align-center justify-space-between">
              <span>Loan Records</span>
              <v-btn
                v-if="$insertAccess($route.fullPath)"
                color="primary"
                variant="tonal"
                prepend-icon="mdi-plus"
                rounded="xl"
                @click="router.push({ name: 'add-loan' })"
              >
                Add Loan
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row class="py-2">
                <v-col lg="4" md="4" cols="12">
                  <v-text-field
                    v-model="search"
                    append-inner-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                    density="compact"
                    variant="outlined"
                    bg-color="grey-lighten-4"
                    clearable
                  />
                </v-col>

                <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                        >
                          CSV
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                        >
                          EXCEL
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                        >
                          PDF
                        </v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
              </v-row>

              <v-data-table
                :headers="headers"
                :items="getSchema"
                :loading="loading"
                :search="search"
                :hover="true"
                density="compact"
                v-model:items-per-page="itemsPerPage"
              >
                <template v-slot:item.index="{ index }">{{ index + 1 }}</template>

                <template v-slot:item.code="{ item }">{{ item.code }}</template>

                <template v-slot:item.employee="{ item }">
                  {{ item.employee?.name }} ({{ item.employee?.code || '-' }})
                </template>

                <template v-slot:item.issue_date="{ item }">
                  {{ formatDisplayDate(item.issue_date) }}
                </template>

                <template v-slot:item.refund_start_date="{ item }">
                  {{ formatDisplayDate(item.refund_start_date) }}
                </template>

                <template v-slot:item.loan_amount="{ item }">
                  {{ formatNumber(item.loan_amount) }}
                </template>

                <template v-slot:item.installment_amount="{ item }">
                  {{ formatNumber(item.installment_amount) }}
                </template>

                <template v-slot:item.paid_installments="{ item }">
                  {{ item.paid_installments }} / {{ item.total_installments }}
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="item.status === 1 ? 'success' : 'grey'"
                    size="small"
                  >
                    {{ item.status === 1 ? 'Active' : 'Completed' }}
                  </v-chip>
                </template>

                <template v-slot:item.action="{ item }">
                  <v-btn
                    icon="mdi-pencil"
                    size="small"
                    variant="text"
                    color="warning"
                    @click="editLoan(item)"
                    v-if="$updateAccess($route.fullPath) && (item.paid_installments ?? 0) === 0"
                    class="mr-1"
                  />
                  <v-btn
                    icon="mdi-eye"
                    size="small"
                    variant="text"
                    color="primary"
                    @click="viewLoanDetails(item)"
                    v-if="$updateAccess($route.fullPath)"
                  />
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useField, useForm } from "vee-validate";
import { ref, computed, getCurrentInstance } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import * as yup from "yup";
import formattedDate from "@/plugins/dateFormatter";

export default {
  name: "LoanRecordsPage",

  data: () => ({
    panel: [0],
    search: "",
    itemsPerPage: 10,
    headers: [
      { title: "#SL", key: "index", align: "center", sortable: false },
      { title: "Code", key: "code", align: "left", sortable: false },
      { title: "Employee", key: "employee", align: "left", sortable: false },
      { title: "Issue Date", key: "issue_date", align: "left", sortable: false },
      { title: "Refund Start", key: "refund_start_date", align: "left", sortable: false },
      { title: "Loan Amount", key: "loan_amount", align: "end", sortable: false },
      { title: "Installment", key: "installment_amount", align: "end", sortable: false },
      { title: "Paid/Total", key: "paid_installments", align: "center", sortable: false },
      { title: "Status", key: "status", align: "center", sortable: false },
      { title: "Action", key: "action", align: "center", sortable: false },
    ],
  }),

  created() {
    // if (this.$store.getters.getHrmEmployeeList.length === 0) {
      this.$store.dispatch("getHrmEmployeeList");
    // }
  },

  computed: {
    getSchema() {
      return this.$store.getters.getEmployeeLoanList || [];
    },
    getHrmEmployeeList() {
      return this.$store.getters.getHrmEmployeeList || [];
    },
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const instance = getCurrentInstance();
    const loading = ref(false);

    const { handleSubmit } = useForm({
      validationSchema: yup.object({
        from_date: yup.date().nullable().label("From Date"),
        to_date: yup.date().nullable().label("To Date"),
        employee_id: yup.number().nullable().label("Employee"),
      }),
    });

    const from_date = useField("from_date");
    const to_date = useField("to_date");
    const employee_id = useField("employee_id");

    const loadData = (params = {}) => {
      loading.value = true;
      store
        .dispatch("getEmployeeLoanList", params)
        .catch(() => {
          toast.error("Failed to load loan records.");
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const submitSearch = handleSubmit((values) => {
      const params = {};
      if (values.employee_id) params.employee_id = values.employee_id;
      if (values.from_date) params.from_date = formattedDate(values.from_date);
      if (values.to_date) params.to_date = formattedDate(values.to_date);
      loadData(params);
    });

    const formatDisplayDate = (date) => {
      if (!date) return "-";
      return formattedDate(date);
    };

    const formatNumber = (num) => {
      if (num == null) return "-";
      return Number(num).toLocaleString();
    };

    const viewLoanDetails = (item) => {
      router.push({
        name: "loan-details",
        params: { id: item.id },
      });
    };

    const editLoan = (item) => {
      router.push({
        name: "edit-loan",
        params: { id: item.id },
      });
    };

    // Export functions (same pattern as attendanceSummary.vue)
    const exportToPDF = () => {
      const list = store.getters.getEmployeeLoanList || [];
      let serialNumber = 1;
      const columns = [
        "#SL",
        "Code",
        "Employee",
        "Issue Date",
        "Refund Start",
        "Loan Amount",
        "Installment",
        "Paid/Total",
        "Status",
      ];
      const rows = list.map((item) => [
        serialNumber++,
        item.code,
        item.employee ? `${item.employee.name} (${item.employee.code || "-"})` : "-",
        formatDisplayDate(item.issue_date),
        formatDisplayDate(item.refund_start_date),
        formatNumber(item.loan_amount),
        formatNumber(item.installment_amount),
        `${item.paid_installments ?? 0} / ${item.total_installments ?? 0}`,
        item.status === 1 ? "Active" : "Completed",
      ]);
      instance.proxy.$exportTableToPDF(
        columns,
        rows,
        "Loan Records",
        "loan_records_" + instance.proxy.$fileDate()
      );
    };

    const exportToExcel = () => {
      const list = store.getters.getEmployeeLoanList || [];
      const data = [
        [
          "#SL",
          "Code",
          "Employee",
          "Issue Date",
          "Refund Start",
          "Loan Amount",
          "Installment",
          "Paid/Total",
          "Status",
        ],
      ];
      let serialNumber = 1;
      list.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          item.employee ? `${item.employee.name} (${item.employee.code || "-"})` : "-",
          formatDisplayDate(item.issue_date),
          formatDisplayDate(item.refund_start_date),
          formatNumber(item.loan_amount),
          formatNumber(item.installment_amount),
          `${item.paid_installments ?? 0} / ${item.total_installments ?? 0}`,
          item.status === 1 ? "Active" : "Completed",
        ]);
      });
      instance.proxy.$exportTableToExcel(
        data,
        "loan_records_" + instance.proxy.$fileDate()
      );
    };

    const exportToCSV = () => {
      const list = store.getters.getEmployeeLoanList || [];
      const data = [
        [
          "#SL",
          "Code",
          "Employee",
          "Issue Date",
          "Refund Start",
          "Loan Amount",
          "Installment",
          "Paid/Total",
          "Status",
        ],
      ];
      let serialNumber = 1;
      list.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          item.employee ? `${item.employee.name} (${item.employee.code || "-"})` : "-",
          formatDisplayDate(item.issue_date),
          formatDisplayDate(item.refund_start_date),
          formatNumber(item.loan_amount),
          formatNumber(item.installment_amount),
          `${item.paid_installments ?? 0} / ${item.total_installments ?? 0}`,
          item.status === 1 ? "Active" : "Completed",
        ]);
      });
      instance.proxy.$exportTableToCSV(
        data,
        "loan_records_" + instance.proxy.$fileDate()
      );
    };

    return {
      from_date,
      to_date,
      employee_id,
      submitSearch,
      loading,
      loadData,
      router,
      formatDisplayDate,
      formatNumber,
      viewLoanDetails,
      editLoan,
      exportToPDF,
      exportToExcel,
      exportToCSV,
    };
  },

  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>
